import { Globe, Shield } from "lucide-react";
import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { GrafanaCloudIntegrationFormUpdateIntegrationMutation } from "./__generated__/GrafanaCloudIntegrationFormUpdateIntegrationMutation.graphql";
import { GrafanaCloudIntegrationFormValidateIntegrationMutation } from "./__generated__/GrafanaCloudIntegrationFormValidateIntegrationMutation.graphql";
import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";

const updateIntegrationMutation = graphql`
  mutation GrafanaCloudIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation GrafanaCloudIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

type HostingType = "cloud" | "self_hosted";

interface GrafanaCloudIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
  onHostingTypeChange?: (hostingType: HostingType) => void;
}

export default function GrafanaCloudIntegrationForm({
  onComplete,
  onCancel,
  onHostingTypeChange,
}: GrafanaCloudIntegrationFormProps) {
  const [hostingType, setHostingType] = useState<HostingType>("cloud");
  const [endpoint, setEndpoint] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [gcpServiceAccountEmail, setGcpServiceAccountEmail] = useState("");
  const [gcpIapClientId, setGcpIapClientId] = useState("");
  const [lokiDatasourceUid, setLokiDatasourceUid] = useState("");
  const [prometheusDatasourceUid, setPrometheusDatasourceUid] = useState("");
  const [tempoDatasourceUid, setTempoDatasourceUid] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const [commitMutation, isMutationInFlight] = useMutation<GrafanaCloudIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<GrafanaCloudIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation
  );

  const resetValidation = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);
  };

  const buildCredentials = (): Record<string, string> | null => {
    if (!endpoint.trim()) {
      setFormError("Endpoint is required");
      return null;
    }

    const credentials: Record<string, string> = {
      endpoint: endpoint.trim(),
      hosting_type: hostingType,
    };

    if (hostingType === "cloud") {
      if (!apiToken.trim()) {
        setFormError("API Token is required");
        return null;
      }
      credentials.api_token = apiToken.trim();
    } else {
      if (!gcpServiceAccountEmail.trim()) {
        setFormError("GCP Service Account Email is required");
        return null;
      }
      if (!gcpIapClientId.trim()) {
        setFormError("IAP Client ID is required");
        return null;
      }
      credentials.gcp_service_account_email = gcpServiceAccountEmail.trim();
      credentials.gcp_iap_client_id = gcpIapClientId.trim();
    }

    if (lokiDatasourceUid.trim()) {
      credentials.loki_datasource_uid = lokiDatasourceUid.trim();
    }
    if (prometheusDatasourceUid.trim()) {
      credentials.prometheus_datasource_uid = prometheusDatasourceUid.trim();
    }
    if (tempoDatasourceUid.trim()) {
      credentials.tempo_datasource_uid = tempoDatasourceUid.trim();
    }

    if (!lokiDatasourceUid.trim() && !prometheusDatasourceUid.trim() && !tempoDatasourceUid.trim()) {
      setFormError("At least one datasource UID is required (Loki, Prometheus / Mimir, or Tempo)");
      return null;
    }

    return credentials;
  };

  const handleTest = () => {
    resetValidation();

    const credentials = buildCredentials();
    if (!credentials) return;

    const input = {
      integrationType: "GRAFANA_CLOUD" as IntegrationType,
      credentials: JSON.stringify(credentials)
    };

    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          return;
        }

        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          return;
        }

        setValidationSuccess("✓ Credentials validated successfully!");
        setIsValidated(true);
      },
      onError: error => {
        setFormError(error.message);
      }
    });
  };

  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    const credentials = buildCredentials();
    if (!credentials) return;

    const input = {
      integrationType: "GRAFANA_CLOUD" as IntegrationType,
      credentials: JSON.stringify(credentials)
    };

    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          onComplete(false);
          return;
        }

        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }

        onComplete(true);
      },
      onError: error => {
        setFormError(error.message);
        onComplete(false);
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-4">Authentication</h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Hosting Type</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setHostingType("cloud");
                    setGcpServiceAccountEmail("");
                    setGcpIapClientId("");
                    resetValidation();
                    onHostingTypeChange?.("cloud");
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors ${
                    hostingType === "cloud"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-muted-foreground/50"
                  }`}
                >
                  <Globe className="h-4 w-4" />
                  Cloud
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHostingType("self_hosted");
                    setApiToken("");
                    resetValidation();
                    onHostingTypeChange?.("self_hosted");
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors ${
                    hostingType === "self_hosted"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-muted-foreground/50"
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  Self-hosted (GCP IAP)
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="endpoint" className="text-muted-foreground">Endpoint</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="endpoint"
                    type="text"
                    value={endpoint}
                    onChange={(e) => {
                      setEndpoint(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder={
                      hostingType === "cloud"
                        ? "Enter your Grafana endpoint, e.g. <org_slug>.grafana.net"
                        : "Enter your Grafana endpoint, e.g. https://grafana.example.com"
                    }
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            {hostingType === "cloud" && (
              <div className="space-y-2">
                <Label htmlFor="api-token" className="text-muted-foreground">API Token</Label>
                <Tooltip
                  content={!canModify ? "You don't have permission to modify integrations" : null}
                  position="top"
                  asChild
                >
                  <span className="block w-full">
                    <SecretInput
                      id="api-token"
                      value={apiToken}
                      onChange={(e) => {
                        setApiToken(e.target.value);
                        setIsValidated(false);
                        setValidationSuccess(null);
                      }}
                      placeholder="Enter your Grafana API token"
                      disabled={!canModify}
                    />
                  </span>
                </Tooltip>
              </div>
            )}

            {hostingType === "self_hosted" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="gcp-service-account-email" className="text-muted-foreground">
                    GCP Service Account Email
                  </Label>
                  <Tooltip
                    content={!canModify ? "You don't have permission to modify integrations" : null}
                    position="top"
                    asChild
                  >
                    <span className="block w-full">
                      <Input
                        id="gcp-service-account-email"
                        type="text"
                        value={gcpServiceAccountEmail}
                        onChange={(e) => {
                          setGcpServiceAccountEmail(e.target.value);
                          setIsValidated(false);
                          setValidationSuccess(null);
                        }}
                        placeholder="e.g. grafana-iap@my-project.iam.gserviceaccount.com"
                        disabled={!canModify}
                      />
                    </span>
                  </Tooltip>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gcp-iap-client-id" className="text-muted-foreground">
                    IAP OAuth Client ID
                  </Label>
                  <Tooltip
                    content={!canModify ? "You don't have permission to modify integrations" : null}
                    position="top"
                    asChild
                  >
                    <span className="block w-full">
                      <Input
                        id="gcp-iap-client-id"
                        type="text"
                        value={gcpIapClientId}
                        onChange={(e) => {
                          setGcpIapClientId(e.target.value);
                          setIsValidated(false);
                          setValidationSuccess(null);
                        }}
                        placeholder="e.g. 123456789.apps.googleusercontent.com"
                        disabled={!canModify}
                      />
                    </span>
                  </Tooltip>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-1">Data Sources</h3>
          <p className="text-sm text-muted-foreground mb-4">At least one datasource UID is required.</p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="loki-datasource-uid" className="text-muted-foreground">Loki Datasource UID</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="loki-datasource-uid"
                    type="text"
                    value={lokiDatasourceUid}
                    onChange={(e) => {
                      setLokiDatasourceUid(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder="e.g. grafanacloud-logs"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prometheus-datasource-uid" className="text-muted-foreground">Prometheus / Mimir Datasource UID</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="prometheus-datasource-uid"
                    type="text"
                    value={prometheusDatasourceUid}
                    onChange={(e) => {
                      setPrometheusDatasourceUid(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder="e.g. grafanacloud-prom"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tempo-datasource-uid" className="text-muted-foreground">Tempo Datasource UID</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="tempo-datasource-uid"
                    type="text"
                    value={tempoDatasourceUid}
                    onChange={(e) => {
                      setTempoDatasourceUid(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder="e.g. grafanacloud-traces"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {formError && (
        <ErrorAlert icon="none">
          {formError}
        </ErrorAlert>
      )}

      {validationSuccess && (
        <SuccessAlert icon="none">
          {validationSuccess}
        </SuccessAlert>
      )}

      <div className="flex justify-end space-x-3 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <div className="relative group">
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                onClick={handleSave}
                disabled={!isValidated || isMutationInFlight || isValidationInFlight || !canModify}
              >
                {isMutationInFlight ? "Saving..." : "Connect"}
              </Button>
            </span>
          </Tooltip>
          {!isValidated && !isMutationInFlight && !isValidationInFlight && canModify && (
            <div className="absolute bottom-full mb-2 right-0 px-3 py-2 bg-card text-foreground text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">
              To connect, please test the connection.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
